import { useCallback, useEffect, useRef, useState } from 'react';
import {
  describeError,
  fetchHrCandidates,
  fetchHrComparison,
  fetchHrPeople,
  fetchHrDays,
  fetchHrPeriods,
  fetchDriveState,
  hrExportUrl,
  saveHrToDrive,
  fetchHrSource,
  fetchHrTeams,
  fetchServiceTeams,
  setServiceTeam,
  hrSourceFileUrl,
  hrTeamsFileUrl,
  linkHrPerson,
  uploadHrFile,
  uploadHrTeams,
  type HrComparison,
  type HrComparisonRow,
  type HrDay,
  type HrSource,
  type HrPeriodRow,
  type HrPersonRow,
  type HrPreview,
  type HrTeamsStatus,
  type ServiceTeamRow,
  type DriveUpload,
  type Period,
} from '../api.js';
import { formatDate, formatHours, formatHoursTotal, formatPeriod } from '../format.js';

/**
 * Сверка PeopleForce и Jira.
 *
 * Роли источников неравны: файл бухгалтера считается верным, Jira — тем, что
 * должно с ним сойтись. Поэтому экран ничего не «подтягивает» и не исправляет,
 * он показывает разницу — дальше с ней работают люди.
 */
/**
 * @param canEdit — можно ли этому человеку загружать табель и подтверждать
 * связки. Запрет держит сервер; здесь мы просто не показываем того, чего
 * человеку нельзя, — кнопка, отвечающая «нет прав», хуже отсутствующей.
 */
export function HrReconcile({ canEdit = false }: { canEdit?: boolean }) {
  const [preview, setPreview] = useState<HrPreview>();
  // Исходник открывают по вопросу «а что в файле», поэтому он закрыт
  // по умолчанию и грузится только когда его действительно открыли.
  const [sourceOpen, setSourceOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [source, setSource] = useState<HrSource>();
  const [periods, setPeriods] = useState<HrPeriodRow[]>([]);
  const [comparison, setComparison] = useState<HrComparison>();
  const [people, setPeople] = useState<HrPersonRow[]>([]);
  const [candidates, setCandidates] = useState<{ userKey: string; displayName: string }[]>([]);
  const [period, setPeriod] = useState<{ from: string; to: string }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [note, setNote] = useState<string>();
  const [version, setVersion] = useState(0);
  const [fileName, setFileName] = useState<string>();
  // Состав команд месяца: `null` — файл к месяцу не прикладывали, и весь
  // остаток ушёл в базовую команду. `undefined` — ещё не знаем.
  const [teams, setTeams] = useState<HrTeamsStatus | null>();
  const fileInput = useRef<HTMLInputElement>(null);
  const teamsInput = useRef<HTMLInputElement>(null);
  // Файл держим в ref, а не в state: он нужен только между разбором и записью.
  const pending = useRef<File | undefined>(undefined);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetchHrPeriods(controller.signal),
      fetchHrPeople(controller.signal),
      fetchHrCandidates(controller.signal),
    ]).then(
      ([loadedPeriods, loadedPeople, loadedCandidates]) => {
        setPeriods(loadedPeriods);
        setPeople(loadedPeople);
        setCandidates(loadedCandidates);
        // По умолчанию — самый свежий месяц с данными.
        const latest = loadedPeriods[0];
        if (latest) setPeriod({ from: latest.from, to: latest.to });
      },
      (cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      },
    );
    return () => controller.abort();
  }, [version]);

  useEffect(() => {
    if (!sourceOpen || !period) return;
    setSource(undefined);
    const controller = new AbortController();
    fetchHrSource(period, controller.signal).then(setSource, (cause: unknown) => {
      if (!controller.signal.aborted) setError(describeError(cause));
    });
    return () => controller.abort();
  }, [sourceOpen, period, version]);

  // Смена месяца закрывает исходник и состав: открытыми они относились бы
  // к прошлому месяцу.
  useEffect(() => {
    setSourceOpen(false);
    setTeamsOpen(false);
  }, [period]);

  useEffect(() => {
    if (!period) {
      setTeams(undefined);
      return;
    }
    const controller = new AbortController();
    fetchHrTeams(period, controller.signal).then(setTeams, (cause: unknown) => {
      if (!controller.signal.aborted) setError(describeError(cause));
    });
    return () => controller.abort();
  }, [period, version]);

  useEffect(() => {
    if (!period) return;
    const controller = new AbortController();
    // Разрез один — по команде с задачи (`customfield_11201`). Проектный
    // остался в API, но на экране не нужен: сверку смотрят по командам.
    fetchHrComparison(period, 'team', controller.signal).then(setComparison, (cause: unknown) => {
      if (!controller.signal.aborted) setError(describeError(cause));
    });
    return () => controller.abort();
  }, [period, version]);

  const analyse = useCallback((file: File) => {
    pending.current = file;
    setFileName(file.name);
    setBusy(true);
    setError(undefined);
    setNote(undefined);

    uploadHrFile(file, { dryRun: true }).then(
      (result) => {
        setPreview(result);
        setBusy(false);
      },
      (cause: unknown) => {
        setError(describeError(cause));
        setBusy(false);
      },
    );
  }, []);

  /**
   * Месяц приходит из превью — тот, что подтвердил человек, а не тот, что
   * определился сам. Сервер без него писать откажется (решение 2026-09-07).
   */
  const confirmImport = useCallback((year: number, month: number) => {
    const file = pending.current;
    if (!file) return;
    setBusy(true);

    uploadHrFile(file, { dryRun: false, year, month }).then(
      (result) => {
        setBusy(false);
        setPreview(undefined);
        pending.current = undefined;
        setFileName(undefined);
        if (fileInput.current) fileInput.current.value = '';
        // Подтверждаем ровно то, что могло пойти не так, — **в какой месяц**
        // легло. Дни-строки таблицы человеку ни о чём не говорят, а про замену
        // сказано до записи, в превью: повторять после неё незачем.
        setNote(
          `${MONTHS[month - 1]} ${year} загружен: ${result.saved?.people ?? 0} человек, ` +
            `${result.totals.hours} ч`,
        );
        if (result.period) setPeriod({ from: result.period.from, to: result.period.to });
        setVersion((current) => current + 1);
      },
      (cause: unknown) => {
        setError(describeError(cause));
        setBusy(false);
      },
    );
  }, []);

  /**
   * Выгрузка состава — отдельной загрузкой к уже выбранному месяцу: состав
   * меняется сам по себе, и чтобы поправить команду, перезаливать часы незачем.
   * Разбора с подтверждением здесь нет: файл маленький, а месяц человек уже
   * выбрал — подтверждать нечего.
   */
  const uploadTeams = useCallback((file: File, target: { from: string; to: string }) => {
    const start = new Date(`${target.from}T00:00:00Z`);
    setBusy(true);
    setError(undefined);
    setNote(undefined);

    uploadHrTeams(file, { year: start.getUTCFullYear(), month: start.getUTCMonth() + 1 }).then(
      (result) => {
        setBusy(false);
        if (teamsInput.current) teamsInput.current.value = '';
        setNote(
          `Состав команд загружен: ${result.totals.teams} команд, ` +
            `${result.totals.matched} из ${result.totals.people} человек найдены в табеле` +
            (result.unknownTeams.length
              ? `. Нет среди команд Jira: ${result.unknownTeams.join(', ')}`
              : ''),
        );
        // Панель оставляем открытой: человек только что загрузил файл
        // и должен увидеть, как он лёг, а не искать фишку заново.
        setTeamsOpen(true);
        setVersion((current) => current + 1);
      },
      (cause: unknown) => {
        setError(describeError(cause));
        setBusy(false);
        if (teamsInput.current) teamsInput.current.value = '';
      },
    );
  }, []);

  const link = useCallback((personId: string, userKey: string | null) => {
    linkHrPerson(personId, userKey).then(
      () => setVersion((current) => current + 1),
      (cause: unknown) => setError(describeError(cause)),
    );
  }, []);

  /** Перечитать сверку: пометка команды меняет колонки, а не только список. */
  const refresh = useCallback(() => setVersion((current) => current + 1), []);

  const suggestionsFor = (personId: string) =>
    people.find((person) => person.personId === personId)?.suggestions ?? [];

  /** Строка выбранного месяца: её числа и файлы показывает панель. */
  const selected = periods.find((row) => row.from === period?.from);

  /**
   * Выбор файла табеля. Живёт в панели месяца, а пока месяцев нет — в пустом
   * экране: иначе первую загрузку делать нечем, а панели ещё не существует.
   *
   * Родной `input` прячем, но оставляем в потоке: клавиатура и скринридер
   * работают с ним, а видит человек обычную кнопку.
   */
  const uploadTimesheet = canEdit ? (
    <label className="hr-file hr-month-upload">
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) analyse(file);
        }}
      />
      <span className="hr-file-button">Загрузить табель</span>
      {fileName && <span className="hr-file-name">{fileName}</span>}
    </label>
  ) : null;

  return (
    <section className="revision">
      {!canEdit && (
        <div className="muted small">
          Загрузка файлов и связки доступны тем, кто ведёт сверку. Смотреть и выгружать можно всем.
        </div>
      )}

      {busy && <div className="empty">Разбираю файл…</div>}
      {error && <div className="error">{error}</div>}
      {note && <div className="notice">{note}</div>}

      {preview && (
        <Preview preview={preview} onConfirm={confirmImport} busy={busy} periods={periods} />
      )}

      {periods.length > 0 && (
        /*
         * Панель месяца — одной полосой (решение заказчика 2026-09-23).
         *
         * Раньше над таблицей стояли четыре блока подряд: загрузка файла,
         * месяцы фишками, исходник, состав. Каждый по отдельности был понятен,
         * а вместе они отодвигали числа на второй экран и не показывали
         * главного — что файлы принадлежат **выбранному месяцу**, а не экрану.
         * Здесь всё про месяц собрано в одном месте и в одном порядке чтения:
         * какой месяц → сколько в нём → чем он загружен.
         */
        <div className="hr-month-bar">
          <div className="hr-month-line">
            <label className="hr-month-pick">
              Месяц
              {/* Список, а не фишки: месяцев будет дюжина в год, и строка
                  фишек однажды займёт две полосы вместо одной. Объём и
                  оговорка про неполный табель — в самой строке списка,
                  чтобы выбирать можно было, не открывая месяц. */}
              <select
                value={period?.from ?? ''}
                onChange={(event) => {
                  // Новый месяц в списке взяться не может — там только
                  // загруженные, — а человек ищет его именно здесь: он выбирает
                  // месяц, а не думает про файлы. Поэтому последним пунктом
                  // стоит сама загрузка, и она открывает выбор файла.
                  if (event.target.value === NEW_MONTH) {
                    fileInput.current?.click();
                    return;
                  }
                  const row = periods.find((candidate) => candidate.from === event.target.value);
                  if (row) setPeriod({ from: row.from, to: row.to });
                }}
              >
                {periods.map((row) => (
                  <option key={row.from} value={row.from}>
                    {monthLabel(row.from)} · {row.hours} ч{partialThrough(row) ? ' · неполный' : ''}
                  </option>
                ))}
                {canEdit && <option value={NEW_MONTH}>+ загрузить новый месяц…</option>}
              </select>
            </label>

            {/* Кнопка — сразу за списком: загрузка табеля это тот же вопрос
                «какой месяц», только месяца ещё нет. У правого края она
                читалась бы как действие над уже выбранным. */}
            {uploadTimesheet}

            {selected && (
              <span className="muted small">
                {selected.people} человек · последняя загрузка {formatDate(selected.lastUploadedAt)}
                {selected.imports > 1 ? ` · загружался ${selected.imports} раза` : ''}
              </span>
            )}
          </div>

          <div className="hr-month-line">
            {selected?.hasSource && (
              <button
                type="button"
                className={`chip hr-source${sourceOpen ? ' chip-on' : ''}`}
                onClick={() => setSourceOpen((open) => !open)}
                title={`${selected.lastFileName} — файл PeopleForce, из которого посчитан этот месяц`}
              >
                <span className="hr-source-icon" aria-hidden="true">
                  ▤
                </span>
                {sourceOpen ? 'скрыть исходник' : 'исходник PeopleForce'}
              </button>
            )}
            {/* Фишка состава стоит и когда его нет: именно отсюда его грузят,
                а месяц без состава считается по правилу по умолчанию. */}
            <button
              type="button"
              className={
                `chip hr-source${teamsOpen ? ' chip-on' : ''}` +
                (teams === null ? ' hr-teams-none' : '')
              }
              onClick={() => setTeamsOpen((open) => !open)}
              title={
                teams
                  ? `${teams.fileName} — выгрузка состава команд, по ней разошёлся остаток месяца`
                  : 'Выгрузки состава команд за этот месяц нет: весь остаток ушёл в команду Govitall'
              }
            >
              <span className="hr-source-icon" aria-hidden="true">
                ▤
              </span>
              {teamsOpen ? 'скрыть состав' : teams ? 'состав команд' : 'состава команд нет'}
            </button>
          </div>
        </div>
      )}

      {teamsOpen && period && (
        <TeamsPanel
          period={period}
          teams={teams}
          canEdit={canEdit}
          inputRef={teamsInput}
          onUpload={(file) => uploadTeams(file, period)}
        />
      )}

      {sourceOpen && period && <SourceFile source={source} period={period} />}

      {comparison && (
        <Comparison
          data={comparison}
          suggestionsFor={suggestionsFor}
          candidates={candidates}
          onLink={link}
          canEdit={canEdit}
          onChanged={refresh}
        />
      )}

      {!comparison && periods.length === 0 && !preview && (
        <div className="empty">
          <p>Данных PeopleForce пока нет.</p>
          <p className="muted">Загрузите выгрузку табеля за месяц — дальше появится сверка.</p>
          {uploadTimesheet}
        </div>
      )}
    </section>
  );
}

/** Значение пункта «загрузить новый месяц» — не дата, поэтому с датой не спутать. */
const NEW_MONTH = 'new';

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

/**
 * Месяц выбирает человек, а файл его только предлагает (решение 2026-09-07).
 *
 * До этого месяц определялся сам и сразу записывался. Файл за август определился
 * как июль — в обоих 31 день, увольнений в файле нет, — и запись заменила
 * июльские данные августовскими. Кнопка при этом выглядела ровно так же, как
 * при верном разборе.
 *
 * Поэтому выбор стоит **перед** кнопкой и заполнен предложением из файла:
 * согласиться — один взгляд, поменять — два клика, а промолчать нельзя.
 */
function Preview({
  preview,
  onConfirm,
  busy,
  periods,
}: {
  preview: HrPreview;
  onConfirm: (year: number, month: number) => void;
  busy: boolean;
  /** Месяцы, уже лежащие в базе: по ним видно, что именно заменит запись. */
  periods: HrPeriodRow[];
}) {
  const { totals, detection, period } = preview;
  const matched = preview.people.filter((person) => person.match).length;

  const suggested = period ?? {
    year: new Date().getUTCFullYear(),
    month: new Date().getUTCMonth(),
  };
  const [year, setYear] = useState(suggested.year);
  const [month, setMonth] = useState(suggested.month);

  const chosenFrom = `${year}-${String(month).padStart(2, '0')}-01`;
  const existing = periods.find((row) => row.from === chosenFrom);
  const differs = period ? period.year !== year || period.month !== month : true;

  return (
    <div className="hr-preview">
      <h3>Что в файле</h3>

      <div className="severity-cards">
        {/* Без подсказки: разбор объясняется одной строкой ниже, у выбора
            месяца, где решение и принимают. В карточке тот же текст растягивал
            её на пол-экрана и топил в себе остальные пять. */}
        <Card
          label="Период по файлу"
          value={period ? formatPeriod(period.from, period.to) : 'не определён'}
        />
        <Card label="Сотрудников" value={String(totals.people)} />
        <Card label="Часов" value={String(totals.hours)} />
        <Card
          label="Отпуск / больничный"
          value={`${totals.vacationDays} / ${totals.sickDays}`}
          hint="дней с пометкой"
        />
        <Card
          label="Приняты / уволены"
          value={`${totals.hiredInPeriod} / ${totals.leftInPeriod}`}
          hint="внутри периода"
        />
        <Card
          label="Сопоставлены с Jira"
          value={`${matched} из ${totals.people}`}
          hint="точное совпадение имени; остальных свяжете вручную ниже"
        />
      </div>

      <div className="hr-month">
        <label>
          <span className="chip-group-label">Записать как месяц</span>
          <select value={month} onChange={(event) => setMonth(Number(event.target.value))}>
            {MONTHS.map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
          <input
            type="number"
            className="hr-year"
            value={year}
            min={2000}
            max={2100}
            onChange={(event) => setYear(Number(event.target.value))}
          />
        </label>

        <div className="muted small">
          По содержимому файла: {detection.evidence.join('; ')}.
          {!detection.confident && ' Признак слабый — проверьте месяц перед записью.'}
        </div>

        {differs && (
          <div className="notice small">
            Выбран месяц, отличный от определённого по файлу. Так и задумано, если файл
            переименовали или разбор ошибся, — но это стоит перепроверить.
          </div>
        )}

        {/* Замена — не побочный эффект, а штатный способ внести исправление:
            в PeopleForce правят задним числом, и перезалить месяц свежим файлом
            это нормальная работа. Но знать о замене надо **до** неё, а не из
            строки «заменено 1426 дней» после. */}
        {existing && (
          <div className="notice small">
            За {MONTHS[month - 1]} {year} данные уже есть: {existing.people} человек,{' '}
            {existing.hours} ч. Запись заменит их целиком — так и вносят исправления, сделанные в
            PeopleForce задним числом.
          </div>
        )}
      </div>

      {preview.warnings.some((warning) => warning.kind === 'period_mismatch') && (
        <div className="error small">
          {preview.warnings.find((warning) => warning.kind === 'period_mismatch')?.message}
        </div>
      )}

      {preview.warnings.length > 0 && (
        <details className="hr-warnings">
          <summary>Замечания по файлу: {preview.warnings.length}</summary>
          <ul>
            {preview.warnings.map((warning, position) => (
              <li key={`${warning.kind}-${position}`}>
                {warning.row ? <span className="muted">строка {warning.row}: </span> : null}
                {warning.message}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="matrix-scroll">
        <table className="matrix revision-table">
          <thead>
            <tr>
              <th>Таб. №</th>
              <th>Сотрудник</th>
              <th>Департамент</th>
              <th>Занятость</th>
              <th className="num">Часов</th>
              <th className="num">Отпуск</th>
              <th className="num">Больничный</th>
              <th>Учётка Jira</th>
            </tr>
          </thead>
          <tbody>
            {preview.people.map((person) => (
              <tr key={person.personId} className={person.seconds === 0 ? 'idle-row' : undefined}>
                <td className="muted">{person.personId}</td>
                <td>{person.fullName}</td>
                <td>{person.department ?? '—'}</td>
                <td>{person.employmentType ?? '—'}</td>
                <td className="num">{formatHoursTotal(person.seconds)}</td>
                <td className="num">{person.vacationDays || '—'}</td>
                <td className="num">{person.sickDays || '—'}</td>
                <td>
                  {person.match ? (
                    person.match.displayName
                  ) : person.suggestions.length > 0 ? (
                    <span className="muted">похоже на {person.suggestions[0]?.displayName}</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        {/* Месяц в подписи кнопки, а не только в селекте выше: подтверждают
            в этот момент именно его, и он должен быть перед глазами. */}
        <button
          type="button"
          className="refresh"
          onClick={() => onConfirm(year, month)}
          disabled={busy}
        >
          Загрузить как {MONTHS[month - 1]} {year}
        </button>{' '}
        <span className="muted small">
          Повторная загрузка того же месяца заменяет его целиком — в PeopleForce правят задним
          числом.
        </span>
      </div>
    </div>
  );
}

/**
 * Раскрыт ли блок пояснений. Единственное, что экран помнит между заходами,
 * и намеренно: это предпочтение чтения, а не данные. Обращение обёрнуто —
 * в приватном окне и при запрете хранилища доступ к нему бросает исключение,
 * и падать из-за подсказки было бы глупо.
 */
const LEGEND_KEY = 'hr.legend.open';

function readLegendOpen(): boolean {
  try {
    return localStorage.getItem(LEGEND_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLegendOpen(open: boolean): void {
  try {
    localStorage.setItem(LEGEND_KEY, open ? '1' : '0');
  } catch {
    // Хранилище недоступно — блок просто не запомнит состояние.
  }
}

function Comparison({
  data,
  suggestionsFor,
  candidates,
  onLink,
  canEdit,
  onChanged,
}: {
  data: HrComparison;
  suggestionsFor: (personId: string) => { userKey: string; displayName: string; score: number }[];
  candidates: { userKey: string; displayName: string }[];
  onLink: (personId: string, userKey: string | null) => void;
  canEdit: boolean;
  onChanged: () => void;
}) {
  // Итогов из ответа здесь нет: подвал считается по показанным строкам, а сводка
  // карточками убрана — числа живут в таблице, где видно, из чего они сложены.
  const { columns } = data;
  const partial = partialThrough({ to: data.period.to, filledTo: data.filledTo });

  // Пояснения по умолчанию свёрнуты, но выбор человека переживает
  // перезагрузку: правило читают один раз, а закрывать блок каждое утро —
  // это раздражение без пользы.
  const [legendOpen, setLegendOpen] = useState(readLegendOpen);
  const onLegendToggle = useCallback((event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const open = event.currentTarget.open;
    setLegendOpen(open);
    writeLegendOpen(open);
  }, []);

  // Раскрыта одна строка за раз: две открытые сетки по 31 колонке рядом
  // не сравнивают — их листают.
  const [openPerson, setOpenPerson] = useState<string>();
  const [days, setDays] = useState<HrDay[]>();

  useEffect(() => {
    if (!openPerson) return;
    setDays(undefined);
    const controller = new AbortController();
    fetchHrDays(data.period, openPerson, controller.signal).then(
      (result) => setDays(result.days),
      () => undefined,
    );
    return () => controller.abort();
  }, [openPerson, data.period]);

  const onToggle = (personId: string) =>
    setOpenPerson((current) => (current === personId ? undefined : personId));
  const [query, setQuery] = useState('');

  /**
   * Google Диск. Пока он не настроен, `configured` приходит `false`
   * и кнопки нет: обещать действие, которого не произойдёт, хуже, чем
   * не предлагать его вовсе.
   */
  const [drive, setDrive] = useState<{ configured: boolean; uploads: DriveUpload[] }>();
  const [saving, setSaving] = useState(false);
  const [driveError, setDriveError] = useState<string>();

  useEffect(() => {
    const controller = new AbortController();
    setDriveError(undefined);
    fetchDriveState(data.period, controller.signal).then(
      (state) => setDrive({ configured: state.configured, uploads: state.uploads }),
      () => setDrive({ configured: false, uploads: [] }),
    );
    return () => controller.abort();
  }, [data.period]);

  const onSaveToDrive = useCallback(() => {
    setSaving(true);
    setDriveError(undefined);
    saveHrToDrive(data.period, data.dimension).then(
      (upload) => {
        setSaving(false);
        // Журнал пополняется сразу: иначе человек нажмёт второй раз, не поверив.
        // Отдельной строки «сохранено» больше нет — только что положенный файл
        // и так стоит первым в этом журнале, а две строки об одном событии
        // это ровно та лишняя информация, которая рассеивает внимание.
        setDrive((state) => ({
          configured: true,
          uploads: [upload, ...(state?.uploads ?? [])],
        }));
      },
      (cause: unknown) => {
        setSaving(false);
        setDriveError(describeError(cause));
      },
    );
  }, [data.period, data.dimension]);

  // Сначала самое большое расхождение: ради него экран и открывают.
  const sorted = [...data.rows].sort((a, b) => a.diffSeconds - b.diffSeconds);
  const rows = sorted.filter((row) => matchesQuery(row, query));

  // Итоги считаются по показанным строкам, а не берутся из ответа: иначе при
  // поиске подвал показывал бы сумму по всем, и одна строка «не сходилась» бы
  // с итогом прямо под ней.
  // Вместе с остатком: в колонке команды подразделения он стоит в ячейках,
  // и итог под ними обязан быть их суммой.
  const shownColumnSeconds = (columnId: string) =>
    rows.reduce((sum, row) => sum + (row.byColumn[columnId] ?? 0) + remainderIn(row, columnId), 0);
  const shownJira = rows.reduce((sum, row) => sum + (row.userKey ? row.jiraSeconds : 0), 0);
  const shownHr = rows.reduce((sum, row) => sum + row.hrSeconds, 0);
  const shownAbsence = rows.reduce((sum, row) => sum + row.absenceSeconds, 0);
  const filtered = query.trim() !== '';

  return (
    <div className="hr-comparison">
      {/* Карточек со сводкой здесь нет (решение 2026-09-07). Часы PF и Jira
          повторяли подвал таблицы, «вне расчёта» — именной список под ней,
          а счётчики «не списали» и «не сопоставлены» пересказывали то, что
          видно в самих строках. Единственное, что стоило сохранить, —
          оговорку про итог Jira, и она ушла в легенду, где её читают вместе
          с колонкой. */}

      {/* Легенда, а не абзац: пять определений подряд в сплошном тексте
          никто не дочитывает, а без них таблицу читают неверно. */}
      {/* Неполный табель меняет чтение всей таблицы, поэтому говорим об этом
          над ней, а не в подсказке: иначе пустые колонки остатка и обгоняющие
          часы Jira выглядят как расхождение, которого нет. */}
      {partial && (
        <div className="notice small">
          Табель заполнен по {partial} — месяц ещё ведут, числа неокончательные. Там, где часы Jira
          обгоняют табель, остаток обнуляется: это не расхождение, а незаполненный табель.
        </div>
      )}
      {/* Команда из выгрузки состава, которой нет в Jira: деньги лежат на своём
          месте — колонка заведена по названию из файла, — но названия разошлись,
          и в Jira этой команды нет вовсе. Молчать нельзя. */}
      {data.unmatchedTeams.length > 0 && (
        <div className="notice small">
          Команд {data.unmatchedTeams.join(', ')} среди команд Jira нет — колонка заведена по
          названию из выгрузки состава, и часов Jira в ней не будет, пока команда не появится в
          задачах.
        </div>
      )}

      {/* Пояснения читают один раз, а место они занимали всегда — около 200 px
          над таблицей. Поэтому раскрывашка, а в её строке — то единственное,
          что спрашивают, глядя на подсвеченную ячейку. Выбор запоминается:
          тот, кто правило знает, не должен закрывать блок каждый раз. */}
      <details className="hr-legend-box" open={legendOpen} onToggle={onLegendToggle}>
        <summary>
          {/* Своя стрелка вместо системного треугольника: в проекте раскрывашки
              уже помечены так (фильтры, «Уволены»), и в разных браузерах
              родной маркер выглядит по-разному. */}
          <span className="disclosure-arrow">{legendOpen ? '▾' : '▸'}</span>
          <span className="hr-legend-title">Как читать таблицу</span>
          <span className="hr-legend-gist muted">
            остаток табеля прибавлен к колонке команды сотрудника — такие ячейки подсвечены
          </span>
        </summary>
        <dl className="hr-legend">
          <div>
            <dt>Источники</dt>
            <dd>
              PeopleForce считается верным источником, Jira — тем, что должно с ним сойтись. Экран
              показывает разницу и ничего не исправляет сам: расхождение разбирают люди
            </dd>
          </div>
          <div>
            <dt>Команды</dt>
            <dd>
              часы из Jira по полю Team на задаче: для кого делалась работа. Наведите на заголовок —
              в подсказке полный путь команды
            </dd>
          </div>
          <div>
            <dt>Jira</dt>
            <dd>
              сумма этих часов по человеку. В итоге внизу — только сопоставленные: у кого учётка
              Jira не указана, часы неизвестны, а не равны нулю
            </dd>
          </div>
          <div>
            <dt>PF</dt>
            <dd>
              итог из выгрузки PeopleForce — он считается верным. Не засчитываются только часы
              full-time сотрудника в выходной или праздник: у него эти дни нерабочие, и «восьмёрка»
              в них — недосмотр заполнения. У part-time засчитываются: у них нет ни выходных, ни
              праздников. Такие числа помечены точкой, вычет виден в подсказке
            </dd>
          </div>
          <div>
            <dt>Отсут.</dt>
            <dd>
              отпуск, больничный, праздник или отгул без оплаты — дни, в которые не работали. В
              файле у таких дней стоит только пометка, поэтому часы оценены по обычному дню
              сотрудника. В часы PF они не входят и разницу не объясняют — они объясняют, почему сам
              итог меньше полного месяца
            </dd>
          </div>
          <div>
            <dt>Полоса слева</dt>
            <dd>
              <span className="hr-legend-mark hr-legend-over" /> в Jira больше табеля ·{' '}
              <span className="hr-legend-mark hr-legend-unlinked" /> учётка не сопоставлена. «В Jira
              меньше табеля» и «в Jira ничего» метки не имеют — это остаток, и он в колонке его
              команды
            </dd>
          </div>
          <div>
            <dt>Сервисные команды</dt>
            <dd>
              инфраструктура, финансы, юристы, HR, поддержка — их работа делается для компании, а не
              для отдельной команды, поэтому своей колонки у них нет, а часы сложены в Govitall; на
              вкладке «Часы Jira» они видны как есть.
              {data.serviceTeams.length > 0 && (
                <>
                  {' '}
                  За этот период это {data.serviceTeams.map((team) => team.name).join(', ')} —{' '}
                  {formatHoursTotal(data.totals.serviceSeconds)} ч.
                </>
              )}{' '}
              Список — ниже в этом блоке; новое название по умолчанию бизнесовое, молча в Govitall
              ничего не уезжает
            </dd>
          </div>
          <div>
            <dt>Остаток</dt>
            <dd>
              часы из табеля, не разнесённые по задачам Jira: PF минус Jira. Прибавляется к колонке
              команды сотрудника из выгрузки состава (строка PF TEAM): The App Solutions →
              TheAppSolutions, пробелы и регистр не важны. Кого в выгрузке нет — в Govitall: это и
              команда, и сама компания, так что человек вне команд работает на неё. Туда же весь
              остаток месяца, к которому выгрузки состава не приложили. Такие ячейки подсвечены, из
              чего сложено число — в подсказке. Поэтому сумма колонок команд в строке — это часы PF,
              а колонка Jira остаётся только Jira. Остаток есть лишь у сопоставленных: у кого учётки
              нет, часы Jira неизвестны
            </dd>
          </div>
          <div>
            <dt>Вне расчёта</dt>
            <dd>
              те, кто списывал часы в Jira, но в табеле их нет: либо они из другой компании
              холдинга, либо строка пропущена в выгрузке. В таблицу и в итоги по командам не входят,
              но названы поимённо под ней — молчать о них нельзя
            </dd>
          </div>
          <div>
            <dt>Выгрузка</dt>
            <dd>
              в файл идёт весь период целиком, по всем сотрудникам и командам, включая скрытые
              поиском строки: поиск — способ разглядеть строку, а не сузить отчёт. XLSX и кнопка
              Google Диска дают один и тот же файл, собранный тем же кодом, что считает экран
            </dd>
          </div>
        </dl>

        {/* Список — здесь же, а не отдельной раскрывашкой: правило и его
            настройка это одно и то же место, а вторая полоса над таблицей
            стоила бы ещё одного экрана прокрутки. */}
        <ServiceTeams open={legendOpen} canEdit={canEdit} onChanged={onChanged} />
      </details>

      <div className="hr-search">
        <input
          type="search"
          className="search"
          placeholder="Поиск по фамилии, должности, команде или почте"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {filtered && (
          <span className="muted small">
            показано {rows.length} из {sorted.length}
            {rows.length === 0 && ' — никто не найден'}
          </span>
        )}
        {/* Обе выгрузки — одной группой у правого края. Класс `hr-export`
            именно на обёртке: он отодвигает блок от поиска, и повешенный
            на каждую кнопку отдельно разносил бы их по разным углам. */}
        <div className="hr-export">
          {/* Ссылка, а не кнопка с fetch: файл скачивает браузер, нашему коду
              для этого не нужен ни blob в памяти, ни обработчик. */}
          <a
            className="chip"
            href={hrExportUrl(data.period, data.dimension)}
            title="Excel: шапка строки как в выгрузке PeopleForce, закреплённая шапка листа, цветные колонки. В файл идёт весь период целиком, включая скрытые поиском строки"
          >
            Скачать XLSX
          </a>
          {/* А это кнопка: файл собирает и отправляет сервер, браузеру его
              отдавать не нужно. Появляется только когда Диск настроен. */}
          {drive?.configured && canEdit && (
            <button
              type="button"
              className="chip"
              onClick={onSaveToDrive}
              disabled={saving}
              title="Тот же файл — в папку месяца на общем диске компании. В файл идёт весь период целиком, включая скрытые поиском строки"
            >
              {saving ? 'Сохраняю…' : 'Сохранить на Google Диск'}
            </button>
          )}
        </div>
      </div>
      {driveError && <div className="error hr-export-note">Google Диск: {driveError}</div>}
      {/* Что уже лежит на Диске. Это не пояснение, а состояние: без него
          человек не знает, сохранял ли он сверку, и либо не сохраняет, либо
          кладёт пятую копию — папку на диске видно не всем, у кого есть доступ
          к дашборду. Поэтому строка осталась, но сжата до одной: период виден
          на экране, автор ушёл в подсказку ссылки. */}
      {drive && drive.uploads.length > 0 && (
        <div className="muted small hr-export-note">
          На Диске: {drive.uploads.length} {drive.uploads.length === 1 ? 'файл' : 'файлов'},
          последний{' '}
          <a
            href={drive.uploads[0]?.webViewLink}
            target="_blank"
            rel="noopener noreferrer"
            title={`${drive.uploads[0]?.fileName ?? ''}${drive.uploads[0]?.createdBy ? ` · сохранил ${drive.uploads[0].createdBy}` : ''}`}
          >
            {formatDate(drive.uploads[0]?.createdAt.slice(0, 10) ?? '')}
          </a>
        </div>
      )}

      <div className="matrix-scroll hr-matrix-scroll">
        <table className="matrix revision-table hr-matrix">
          <thead>
            <tr>
              <th className="row-num" title="Порядковый номер строки">
                №
              </th>
              <th className="sticky-col">Сотрудник</th>
              {columns.map((column, index) => (
                <th
                  key={column.id}
                  className={`num col-vert${altColumn(index)}`}
                  title={column.name}
                >
                  {/* Названия команд длинные («Infrastructure&Development»),
                      и горизонтально десяток таких колонок не помещается.
                      Полный путь до команды — в подсказке. */}
                  <span>{column.key}</span>
                </th>
              ))}
              <th className="num total-col" title="Итого часов по командам из Jira">
                Jira
              </th>
              {/* Коротко: полное слово вдвое шире собственных чисел, а колонок
                  в матрице и без того много. Расшифровка — в подсказке. */}
              <th className="num hr-pf hr-pf-first" title="Часы по выгрузке PeopleForce">
                PF
              </th>
              {/* «Отсут.» вместо «Не работал»: сокращение с точкой честно
                  показывает, что это ярлык, и не занимает три числа в ширину. */}
              <th
                className="num hr-pf"
                title="Отсутствия: отпуск, больничный, праздник или отгул без оплаты — только дни без часов. Оценка по обычному дню сотрудника"
              >
                Отсут.
              </th>
              <th>Учётка Jira</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const mark = markOf(row);
              // Массив, а не один элемент: под строкой человека может стоять
              // вторая — с его днями из табеля.
              return [
                <tr key={row.personId} className={mark ? `hr-mark-${mark.kind}` : undefined}>
                  {/* Номер по показанному порядку, а не по всей таблице:
                      при поиске «третья строка» должна значить третью на экране. */}
                  <td className="row-num" title={mark?.hint}>
                    {index + 1}
                  </td>
                  <td className="sticky-col">
                    {/* Фамилия — кнопка: клик показывает дни этого человека
                        из табеля. Вопрос «почему у него 92 часа» возникает
                        ровно здесь, у числа, а не на отдельном экране. */}
                    <button
                      type="button"
                      className="hr-row-toggle"
                      onClick={() => onToggle(row.personId)}
                      title="Показать дни из табеля PeopleForce"
                    >
                      {row.fullName}
                    </button>
                    {orgLines(row).map((line) => (
                      <span key={line.kind} className="row-org">
                        <span className={`row-org-label org-${line.kind}`}>{line.label}:</span>{' '}
                        {line.kind === 'email' ? (
                          <a className="row-org-mail" href={`mailto:${line.value}`}>
                            {line.value}
                          </a>
                        ) : (
                          line.value
                        )}
                      </span>
                    ))}
                    {/* Статусы — своими строками, в том же виде «ПОДПИСЬ: значение»,
                        что и поля выше: в одну строку с фамилией они читались
                        как её часть. */}
                    {row.leftAt && (
                      <span className="row-org">
                        <span className="row-org-label org-left">LEFT:</span>{' '}
                        {formatDate(row.leftAt)}
                      </span>
                    )}
                    {!row.inHr && (
                      <span
                        className="row-org"
                        title="Списывал часы в Jira, но в выгрузке PeopleForce его нет"
                      >
                        <span className="row-org-label org-left">NOT IN PF</span>
                      </span>
                    )}
                  </td>
                  {columns.map((column, index) => {
                    // Три источника, и в колонке базовой команды они сходятся
                    // все: часы по её задачам, часы сервисных команд и остаток.
                    const service = serviceIn(row, column.id);
                    const jira = (row.byColumn[column.id] ?? 0) - service;
                    // Остаток — только в колонке его команды из выгрузки состава.
                    const general = remainderIn(row, column.id);
                    const seconds = jira + service + general;
                    // Подсвечиваем примесь, а не «смешанность»: число, собранное
                    // не только из часов этой команды, обязано отличаться на вид.
                    const derived = service > 0 || general > 0;
                    return (
                      <td
                        key={column.id}
                        className={`${seconds ? 'num' : 'num zero'}${altColumn(index)}${derived ? ' hr-with-general' : ''}`}
                        title={
                          derived
                            ? `${row.fullName} · ${column.name}: ` +
                              [
                                `Jira ${formatHoursTotal(jira)} ч`,
                                service > 0
                                  ? `сервисные команды ${formatHoursTotal(service)} ч`
                                  : null,
                                general > 0
                                  ? `остаток ${formatHoursTotal(general)} ч ` +
                                    `(команда PF ${row.pfTeam ?? 'не задана'})`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' + ')
                            : seconds
                              ? `${row.fullName} · ${column.name}`
                              : undefined
                        }
                      >
                        {formatHours(seconds)}
                      </td>
                    );
                  })}
                  <td className="num total-col">{formatHoursTotal(row.jiraSeconds)}</td>
                  <td
                    className="num hr-pf hr-pf-first"
                    title={[
                      row.vacationDays || row.sickDays
                        ? `отпуск ${row.vacationDays} дн., больничный ${row.sickDays} дн.`
                        : '',
                      // Вычет из числа бухгалтера объясняем прямо у числа:
                      // табель — верхний источник, и молча уменьшать его нельзя.
                      row.excludedSeconds
                        ? `не засчитано ${formatHoursTotal(row.excludedSeconds)} ч: ` +
                          'full-time в выходной или праздник'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  >
                    {row.inHr ? formatHoursTotal(row.hrSeconds) : '—'}
                    {row.excludedSeconds > 0 && <span className="hr-excluded">•</span>}
                  </td>
                  <td className="num hr-pf" title={absenceHint(row)}>
                    {row.inHr && row.absenceDays > 0 ? (
                      formatHoursTotal(row.absenceSeconds)
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {row.userKey ? (
                      <span className="hr-link-cell">
                        {row.jiraName ?? row.userKey}
                        {/* Кнопка строкой ниже: рядом с именем она читалась как
                            часть имени и провоцировала случайный клик. */}
                        {row.inHr && canEdit && (
                          <button
                            type="button"
                            className="chip chip-sm"
                            title="Снять связку с учёткой Jira"
                            onClick={() => onLink(row.personId, null)}
                          >
                            снять связку
                          </button>
                        )}
                      </span>
                    ) : canEdit ? (
                      <Linker
                        personId={row.personId}
                        suggestions={suggestionsFor(row.personId)}
                        candidates={candidates}
                        onLink={onLink}
                      />
                    ) : (
                      // Без прав на правку — только факт: связки нет, и сверить
                      // человека не с чем. Кнопка, отвечающая «нельзя», хуже.
                      <span className="muted small">учётка не сопоставлена</span>
                    )}
                  </td>
                </tr>,
                openPerson === row.personId ? (
                  <tr key={`${row.personId}-days`} className="hr-days-row">
                    {/* Во всю ширину таблицы: сетка дней шире любой колонки,
                        а вставлять её в ячейку значит ломать выравнивание. */}
                    <td colSpan={columns.length + 6}>
                      <SourceDays days={days} row={row} />
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
          <tfoot>
            <tr>
              <th className="row-num" />
              <th className="sticky-col">{filtered ? 'Итого по найденным' : 'Итого'}</th>
              {columns.map((column, index) => (
                <th key={column.id} className={`num${altColumn(index)}`}>
                  {formatHoursTotal(shownColumnSeconds(column.id))}
                </th>
              ))}
              <th className="num total-col">{formatHoursTotal(shownJira)}</th>
              <th className="num hr-pf hr-pf-first">{formatHoursTotal(shownHr)}</th>
              <th className="num hr-pf">{formatHoursTotal(shownAbsence)}</th>
              <th />
            </tr>
          </tfoot>
        </table>
      </div>

      {data.outsiders.length > 0 && (
        <div className="muted small hr-outsiders">
          <strong>Вне расчёта:</strong> {data.outsiders.length} чел.,{' '}
          {formatHoursTotal(data.outsiders.reduce((sum, row) => sum + row.seconds, 0))} ч —{' '}
          {data.outsiders
            .map((row) => `${row.displayName} (${formatHoursTotal(row.seconds)} ч)`)
            .join(', ')}
        </div>
      )}
    </div>
  );
}

/**
 * Список сервисных команд — раскрывашкой под таблицей (решение 2026-10-20).
 *
 * Под таблицей, а не над ней: правило читают один раз, а правят ещё реже.
 * Над таблицей это отодвинуло бы числа на второй экран — та же причина,
 * по которой свернули пояснения и убрали карточки сводки.
 *
 * Список один на два источника названий: команды приходят из Jira, департаменты
 * из выгрузки PeopleForce. Решение про них одинаковое, поэтому и экран один,
 * а откуда название — говорит подпись у строки.
 */
function ServiceTeams({
  open,
  canEdit,
  onChanged,
}: {
  /** Раскрыта ли вся инструкция: по ней и грузим список. */
  open: boolean;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [teams, setTeams] = useState<ServiceTeamRow[]>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState<string>();

  const load = useCallback(() => {
    fetchServiceTeams().then(
      (rows) => {
        setTeams(rows);
        setError(undefined);
      },
      (cause: unknown) => setError(describeError(cause)),
    );
  }, []);

  // Грузим по раскрытию инструкции, а не вместе со сверкой: список нужен
  // тем, кто его правит, а это раз в месяц. Лишний запрос на каждом открытии
  // вкладки дороже, чем ожидание в полсекунды у того, кто пришёл его менять.
  useEffect(() => {
    if (open && !teams) load();
  }, [open, teams, load]);

  const toggle = (row: ServiceTeamRow) => {
    setSaving(row.name);
    setServiceTeam(row.name, !row.service, row.source).then(
      () => {
        setSaving(undefined);
        load();
        // Пометка меняет сами колонки таблицы, поэтому перечитываем и сверку.
        onChanged();
      },
      (cause: unknown) => {
        setSaving(undefined);
        setError(describeError(cause));
      },
    );
  };

  const service = teams?.filter((row) => row.service) ?? [];
  // Команда, которой нет ни в «Підрозділ», ни в выгрузке состава: PeopleForce
  // про неё ничего не знает, и разбирать надо именно такие.
  const unknown =
    teams?.filter((row) => !row.service && row.inJira && !row.inPfTeams && !row.inPfUnit) ?? [];
  // Остальные: подтверждённые PeopleForce плюс названия, которых в задачах
  // Jira нет вовсе. Группа-остаток намеренно ловит всё, что не попало выше, —
  // строка, выпавшая из всех списков, выглядела бы как потерянная.
  const rest =
    teams?.filter((row) => !row.service && !(row.inJira && !row.inPfTeams && !row.inPfUnit)) ?? [];

  return (
    <div className="hr-service">
      <p className="hr-service-title">Список сервисных команд</p>

      {error && <div className="notice small">{error}</div>}
      {!teams && !error && <div className="muted small">загрузка…</div>}

      {teams && (
        <>
          <p className="muted small">
            Отмеченная команда колонки в сверке не получает — её часы складываются в Govitall.
            Понятия «сервисная» в PeopleForce нет, поэтому прийти оттуда признак не может: PF
            отвечает на обратный вопрос, какие команды точно бизнесовые — это «Підрозділ» табеля и
            выгрузка состава. Названия вроде Finance и Support есть только в Jira, и решение по ним
            принимает человек.
            {!canEdit && ' Менять список может тот, кто ведёт сверку.'}
          </p>

          <p className="hr-service-head">
            Сервисные <span className="muted small">— часы уходят в Govitall</span>
          </p>
          <ServiceTeamList rows={service} canEdit={canEdit} saving={saving} onToggle={toggle} />

          {/* Главная группа для разбора: про эти команды PeopleForce ничего
              не знает, и сервисные сидят именно здесь. */}
          <p className="hr-service-head">
            Известны только Jira{' '}
            <span className="muted small">— PF про них ничего не говорит, разбирать здесь</span>
          </p>
          <ServiceTeamList rows={unknown} canEdit={canEdit} saving={saving} onToggle={toggle} />

          <p className="hr-service-head">
            Остальные{' '}
            <span className="muted small">
              — подтверждены PeopleForce либо в задачах Jira не встречаются
            </span>
          </p>
          <ServiceTeamList rows={rest} canEdit={canEdit} saving={saving} onToggle={toggle} />
        </>
      )}
    </div>
  );
}

/** Откуда известно название — словами, а не значками: значки пришлось бы объяснять. */
function originOf(row: ServiceTeamRow): string {
  const parts = [
    row.inJira ? 'Jira' : null,
    row.inPfTeams ? 'состав PF' : null,
    row.inPfUnit ? 'підрозділ' : null,
    row.inPfDepartment ? 'департамент' : null,
  ].filter(Boolean);
  return parts.join(' · ') || 'только пометка';
}

function ServiceTeamList({
  rows,
  canEdit,
  saving,
  onToggle,
}: {
  rows: ServiceTeamRow[];
  canEdit: boolean;
  saving: string | undefined;
  onToggle: (row: ServiceTeamRow) => void;
}) {
  if (rows.length === 0) return <p className="muted small">— никого</p>;

  return (
    <div className="hr-service-list">
      {rows.map((row) => (
        <label key={row.name} className="hr-service-item">
          <input
            type="checkbox"
            checked={row.service}
            disabled={!canEdit || saving === row.name}
            onChange={() => onToggle(row)}
          />
          <span className="hr-service-name">{row.name}</span>
          {/* Часы — вес решения: отметить команду с тысячей часов это не то же,
              что отметить команду с одним часом. */}
          <span className="muted small">
            {row.seconds > 0 ? `${formatHoursTotal(row.seconds)} ч` : '—'}
          </span>
          <span
            className="muted small"
            title={
              row.inJira
                ? undefined
                : 'В задачах Jira такой команды нет: часов за ней не числится, и пометка ни на что не влияет'
            }
          >
            {row.inJira ? originOf(row) : `${originOf(row)} · не действует`}
          </span>
          {row.markedBy && (
            <span className="muted small" title={`отметил ${row.markedBy}`}>
              {row.markedBy}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}

/**
 * Ручная связка. Предложение показывается кнопкой, но автоматически не
 * применяется: перепутать двух людей здесь дороже, чем не найти никого —
 * чужие часы молча уедут в чужую строку.
 */
function Linker({
  personId,
  suggestions,
  candidates,
  onLink,
}: {
  personId: string;
  suggestions: { userKey: string; displayName: string; score: number }[];
  candidates: { userKey: string; displayName: string }[];
  onLink: (personId: string, userKey: string | null) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <span className="hr-linker">
      {suggestions.slice(0, 1).map((suggestion) => (
        <button
          key={suggestion.userKey}
          type="button"
          className="chip"
          title={`Похоже на ${Math.round(suggestion.score * 100)}%`}
          onClick={() => onLink(personId, suggestion.userKey)}
        >
          {suggestion.displayName} ?
        </button>
      ))}
      {open ? (
        <select
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) onLink(personId, event.target.value);
            setOpen(false);
          }}
        >
          <option value="">выберите учётку…</option>
          {candidates.map((candidate) => (
            <option key={candidate.userKey} value={candidate.userKey}>
              {candidate.displayName}
            </option>
          ))}
        </select>
      ) : (
        <button
          type="button"
          className="chip chip-sm chip-todo"
          title="Пока учётка не указана, сверять этого человека не с чем"
          onClick={() => setOpen(true)}
        >
          выбрать учётку
        </button>
      )}
    </span>
  );
}

/**
 * Плитка со сводным числом.
 *
 * Компактный вариант общей карточки: на этой вкладке их пять-шесть подряд,
 * и в полный рост они съедали экран до того, как начиналась сама сверка.
 * Пояснение остаётся видимым — «только по сопоставленным» меняет смысл числа,
 * и прятать это в подсказку нельзя.
 */
function Card({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="severity-card card-compact" title={hint}>
      <span className="severity-name">{label}</span>
      <span className="severity-count">{value}</span>
      {hint && <span className="severity-hours">{hint}</span>}
    </div>
  );
}

/**
 * Организационные строки под фамилией, с подписями.
 *
 * `TEAM` — «Департамент» выгрузки PeopleForce (`Legal`, `Marketing Team`,
 * `Slidepeak`), подпись из табеля. `PF TEAM` — команда из **выгрузки состава**,
 * и это не подпись, а адрес: именно в её колонку лёг остаток человека
 * (решение 2026-09-22). Пусто — человека нет ни в одной команде месяца,
 * и остаток ушёл в базовую команду.
 *
 * Совпадающие значения **не схлопываются**: строки всегда на своих местах, и
 * глаз находит команду в одном и том же месте у каждого человека. Одинаковые
 * «Mental Division» дважды — это не шум, а факт: департамент и команда совпали.
 *
 * Формат работы (`Full-time (office)`, `Part-time`) нужен по той же причине:
 * без него итог парттаймера читается как недоработка.
 */
type OrgKind = 'position' | 'team' | 'company' | 'format' | 'email';

function orgLines(row: HrComparisonRow): { kind: OrgKind; label: string; value: string }[] {
  const fields: { kind: OrgKind; label: string; value: string | null }[] = [
    { kind: 'team', label: 'TEAM', value: row.department },
    { kind: 'company', label: 'PF TEAM', value: row.pfTeam },
    { kind: 'position', label: 'POSITION', value: row.position },
    // Формат работы объясняет чужой на вид итог: 92 часа у парттайма — это
    // норма месяца, а не половина невыполненной работы.
    { kind: 'format', label: 'FORMAT', value: row.employmentType },
    // Почта единственная приходит не из выгрузки, а из связанной учётки Jira,
    // и потому же полезна: неверную связку она выдаёт сразу, глазами.
    { kind: 'email', label: 'EMAIL', value: row.jiraEmail },
  ];

  return fields
    .map((field) => ({ ...field, value: field.value?.trim() ?? '' }))
    .filter((field) => field.value !== '');
}

/**
 * Тональная шахматка по колонкам: через одну.
 *
 * В матрице из полутора десятков узких колонок глаз теряет, к какой из них
 * относится число, — особенно когда в строке одни прочерки. Полосы делают
 * колонку видимой без линий-разделителей, которые в плотной таблице шумят.
 */
function altColumn(index: number): string {
  return index % 2 === 1 ? ' col-alt' : '';
}

/**
 * Поиск по строке: 48 человек в матрице шире экрана — листать их глазами
 * дороже, чем набрать три буквы. Ищем по всему, чем человека называют:
 * фамилия из файла, имя учётки Jira, табельный номер, команда, должность, почта.
 */
function matchesQuery(row: HrComparisonRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;

  return [
    row.fullName,
    row.jiraName,
    row.personId,
    row.department,
    row.pfTeam,
    row.position,
    row.employmentType,
    row.jiraEmail,
  ].some((value) => value?.toLowerCase().includes(needle));
}

/** Из чего сложилась оценка: дни по видам плюс длина обычного дня. */
function absenceHint(row: HrComparisonRow): string | undefined {
  if (!row.inHr || row.absenceDays === 0) return undefined;
  const parts: string[] = [];
  if (row.vacationDays) parts.push(`отпуск ${row.vacationDays} дн.`);
  if (row.sickDays) parts.push(`больничный ${row.sickDays} дн.`);
  if (row.holidayDays) parts.push(`праздник ${row.holidayDays} дн.`);
  return `${parts.join(', ')} × ${formatHours(row.typicalDaySeconds)} ч обычного дня`;
}

/**
 * Цвет разницы. Минус красный — часы, не дописанные в Jira, и таких строк
 * большинство. Плюс синий — редкий и противоположный случай: в Jira списано
 * больше, чем засчитал табель. Оба расхождения, но разной природы, и одинаковый
 * цвет читался бы как «одна и та же проблема».
 */
/**
 * Неразнесённые часы человека: то, что он отработал по табелю, но не отнёс
 * ни к одной задаче Jira. В таблице прибавлены к колонке команды его
 * подразделения.
 *
 * Считается вычитанием — PF минус Jira, — но читается как обычная команда,
 * а не как расхождение со знаком: это часы, которые надо куда-то отнести,
 * и они относятся сюда.
 *
 * Отрицательного здесь быть не может: если в Jira списано больше табеля,
 * неразнесённых часов просто нет. Такой случай виден в колонках Jira и PF
 * рядом, и придумывать ему минус незачем.
 */
function generalSeconds(row: HrComparisonRow): number {
  return Math.max(0, row.hrSeconds - row.jiraSeconds);
}

/**
 * Остаток строки в этой колонке: ненулевой только у его команды. Какая это
 * команда, решил сервер — экран сопоставления не знает.
 *
 * Только у сопоставленных: у человека без учётки часы Jira неизвестны,
 * и объявить весь его табель неразнесённым значило бы выдумать число.
 */
function remainderIn(row: HrComparisonRow, columnId: string): number {
  return row.generalColumn === columnId && row.userKey && row.inHr ? generalSeconds(row) : 0;
}

/**
 * Часы сервисных команд этого человека в этой колонке (решение 2026-10-20).
 *
 * Своей колонки у сервисной команды нет: её работа делается для компании,
 * а не для отдельной команды, поэтому часы лежат в базовой. Какие команды
 * сервисные, решил сервер — экран только рисует и расшифровывает.
 */
function serviceIn(row: HrComparisonRow, columnId: string): number {
  return row.serviceColumn === columnId ? row.serviceSeconds : 0;
}

/**
 * Состав команд месяца: кто загружен, сколько человек нашлось и кого не нашли.
 *
 * Отдельным блоком под месяцами, а не рядом с загрузкой табеля: файл
 * принадлежит **выбранному месяцу**, и до выбора месяца ему некуда лечь.
 *
 * Месяц без состава — не ошибка: весь остаток такого месяца уходит в базовую
 * команду, и так посчитаны все месяцы до сентября 2026. Но сказать об этом
 * надо: иначе колонка Govitall выглядит странно большой без объяснения.
 */
function TeamsPanel({
  period,
  teams,
  canEdit,
  inputRef,
  onUpload,
}: {
  period: { from: string; to: string };
  teams: HrTeamsStatus | null | undefined;
  canEdit: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onUpload: (file: File) => void;
}) {
  return (
    <div className="hr-upload hr-teams">
      {canEdit && (
        <label className="hr-file">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onUpload(file);
            }}
          />
          <span className="hr-file-button">
            {teams ? 'Заменить состав команд' : 'Загрузить состав команд'}
          </span>
        </label>
      )}

      {teams === undefined ? null : teams === null ? (
        <span className="small hr-teams-missing">
          Состав команд за {monthLabel(period.from)} не загружен — весь остаток месяца в команде
          Govitall. Выгрузка команд из PeopleForce, CSV: название, менеджер, участники.
        </span>
      ) : (
        <span className="small">
          Состав команд за {monthLabel(period.from)}: {teams.teams} команд, {teams.matched} из{' '}
          {teams.people} человек найдены в табеле. Остальные — в Govitall.{' '}
          {teams.hasSource && (
            <a href={hrTeamsFileUrl(period)} download>
              {teams.fileName}
            </a>
          )}{' '}
          <span className="muted">от {formatDate(teams.uploadedAt)}</span>
          {/* Кого из файла нет в табеле — поимённо: это либо не тот месяц,
              либо пропущенная строка у бухгалтера, и различает их человек. */}
          {teams.unmatched.length > 0 && (
            <>
              {' '}
              <span className="muted">
                Нет в табеле месяца: {teams.unmatched.map((person) => person.name).join(', ')}
              </span>
            </>
          )}
        </span>
      )}
    </div>
  );
}

function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

/**
 * По какое число заполнен табель, если это раньше конца месяца, — иначе пусто.
 *
 * Считается по данным, а не по календарю: закрытый месяц с недозаполненным
 * хвостом так же неокончателен, как текущий. То же правило, что в `partialNote`
 * на сервере, — и оно должно совпадать: экран и файл не вправе расходиться
 * в том, полны данные или нет.
 */
function partialThrough(row: { to: string; filledTo: string | null }): string {
  if (!row.filledTo) return '';
  // Период полуоткрытый: последний день месяца — это `to` минус сутки.
  const lastDay = new Date(new Date(row.to).getTime() - 86_400_000);
  if (new Date(row.filledTo) >= lastDay) return '';
  return formatDate(row.filledTo);
}

/**
 * Дни человека из табеля PeopleForce — то, что стоит в его строке файла.
 *
 * Показывается как есть: часы, где часы, и сырой текст пометки, где пометка.
 * Ни округлений, ни пересчётов — это исходник, и именно им отвечают на вопрос
 * «почему у него столько».
 */
function SourceDays({ days, row }: { days: HrDay[] | undefined; row: HrComparisonRow }) {
  if (!days) return <span className="muted small">Загружаю дни…</span>;
  if (days.length === 0) {
    return <span className="muted small">В табеле за этот месяц строки нет.</span>;
  }

  const hours = days.reduce((sum, day) => sum + day.seconds, 0) / 3600;

  return (
    <div className="hr-days">
      <div className="muted small">
        {row.fullName} · табель PeopleForce за месяц: {formatHoursTotal(hours * 3600)} ч
      </div>
      <div className="hr-days-grid">
        {days.map((day) => {
          const number = Number(day.date.slice(8, 10));
          // Выходной по данным, а не по календарю: пустой день без пометки.
          const empty = day.seconds === 0 && !day.marker;
          return (
            <div
              key={day.date}
              className={`hr-day${empty ? ' hr-day-empty' : ''}${day.marker ? ` hr-day-${day.markerKind ?? 'other'}` : ''}`}
              title={day.marker ? `${day.date}: ${day.marker}` : day.date}
            >
              <span className="hr-day-num">{number}</span>
              <span className="hr-day-value">
                {day.seconds > 0 ? formatHours(day.seconds) : day.marker ? '•' : '—'}
              </span>
            </div>
          );
        })}
      </div>
      {/* Пометки словами: значок в клетке говорит «здесь что-то есть»,
          а что именно — читают отсюда. */}
      {days.some((day) => day.marker) && (
        <div className="muted small hr-days-markers">
          {[...new Set(days.filter((day) => day.marker).map((day) => day.marker))].join(' · ')}
        </div>
      )}
    </div>
  );
}

/**
 * Исходный файл PeopleForce как есть — таблицей на странице.
 *
 * Не скачивание: скачанный CSV надо ещё чем-то открыть, а вопрос «что
 * в исходнике» возникает прямо во время сверки. Показывается ровно то, что
 * лежит в файле, — ни округлений, ни пересчётов, ни наших подписей.
 */
function SourceFile({ source, period }: { source: HrSource | undefined; period: Period }) {
  if (!source) return <div className="empty">Открываю исходник…</div>;

  return (
    <div className="hr-source-view">
      <div className="hr-source-head muted small">
        <span>
          {source.fileName} · загружен {formatDate(source.uploadedAt)} · строк: {source.rows.length}
        </span>
        {/* Скачивание — здесь же, у имени файла: на экране смотрят «что в нём»,
            а скачивают, чтобы приложить к письму или открыть в Excel. */}
        <a className="chip hr-source-download" href={hrSourceFileUrl(period)} download>
          ↓ скачать CSV
        </a>
      </div>
      {/* Прокрутка по обеим осям: в файле сорок с лишним колонок, и ужимать
          их значит перестать показывать файл таким, какой он есть. */}
      <div className="matrix-scroll hr-source-scroll">
        <table className="matrix hr-source-table">
          <thead>
            <tr>
              {source.header.map((title, index) => (
                <th key={`${title}-${index}`}>{title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {source.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {source.header.map((_, cell) => (
                  <td key={cell}>{row[cell] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Метка строки — полоской слева от номера.
 *
 * Правило переписано 2026-09-08. Раньше жёлтым отмечалось «в Jira меньше, чем
 * в табеле» — а это **норма**, а не сигнал: неразнесённые часы с 4 сентября
 * называются остатком и стоят в колонке его команды. Подсветка за нормальное
 * поведение зажигала одиннадцать строк из сорока шести и приучала не смотреть.
 *
 * 2026-09-11 по той же причине убран красный — «в табеле есть, в Jira ноль».
 * С тех пор как остаток ложится в команду человека, это тоже норма:
 * весь табель такого человека просто уходит в свою команду. Метка горела
 * у половины таблицы (20–26 строк из ~40) и ничего не различала.
 *
 * Осталось два случая, каждый требует чьего-то действия:
 */
function markOf(row: HrComparisonRow): { kind: string; hint: string } | undefined {
  // Учётки нет — сверять нечем, и это незакрытая настройка, а не поведение
  // человека. Отсюда нейтральный цвет: делать надо, но виноватых нет.
  if (!row.userKey) {
    return { kind: 'unlinked', hint: 'Учётка Jira не сопоставлена — сверить не с чем' };
  }
  // Перебор: в Jira часов больше, чем человек отработал по документам.
  // Редкий случай — потому и заметный.
  if (row.jiraSeconds > row.hrSeconds) {
    return { kind: 'over', hint: 'В Jira списано больше, чем в табеле PeopleForce' };
  }
  return undefined;
}
